'use client'

import { type ReactElement, useContext } from 'react'

import { Dialog, type DialogState } from 'ariakit/dialog'
import cn from 'clsx'
import { Button } from '@/components/Button/Button'
import { themes as Theme } from '@/Theme'
import { ThemeContext } from '@/utils/themeContext'
import { IconButton } from '@/components/IconButton/IconButton'
import { Heading } from '@/components/Text/Heading'
import { Text } from '@/components/Text/Text'
import { X } from '@doist/icons/brand'
import { Close } from '@doist/icons/base'
import { ModalFooter } from './ModalFooter'

import css from './Modal.module.css'

export type ModalProps = {
    dialog: DialogState
    title?: string
    aria?: string
    width?: number
    children: React.ReactNode
    closeLabel: string
    className?: string
    primaryButton?: ReactElement<typeof Button>
    secondaryButton?: ReactElement<typeof Button>
}

export function Modal({
    title,
    aria = title || '',
    width = 746,
    dialog,
    children,
    closeLabel,
    primaryButton,
    secondaryButton,
    className,
}: ModalProps) {
    const { theme } = useContext(ThemeContext)
    const isTwist = theme === Theme.twist

    return (
        <Dialog
            state={dialog}
            backdropProps={{ className: css.backdrop }}
            className={cn({ [css.todoist]: !isTwist, [css.twist]: isTwist }, css.popup, className)}
            style={{ width }}
            aria-label={aria}
            tabIndex={0}
        >
            {isTwist ? (
                <>
                    <TwistModalBody
                        dialog={dialog}
                        width={width}
                        aria={aria}
                        title={title}
                        closeLabel={closeLabel}
                    >
                        {children}
                    </TwistModalBody>
                    {(primaryButton || secondaryButton) && (
                        <ModalFooter
                            primaryButton={primaryButton}
                            secondaryButton={secondaryButton}
                        />
                    )}
                </>
            ) : (
                <TodoistModalBody
                    dialog={dialog}
                    width={width}
                    aria={aria}
                    title={title}
                    closeLabel={closeLabel}
                >
                    {children}
                </TodoistModalBody>
            )}
        </Dialog>
    )
}

function TodoistModalBody({ dialog, title, children, closeLabel }: ModalProps) {
    return (
        <>
            {title && (
                <div className={css.header}>
                    <Heading level="2" size="major-base">
                        {title}
                    </Heading>
                    <IconButton
                        aria-label={closeLabel}
                        size="48"
                        radius="50%"
                        icon={Close}
                        onClick={dialog.hide}
                        className={css.closeBtn}
                    />
                </div>
            )}
            <div className={css.contentContainer}>{children}</div>
        </>
    )
}

function TwistModalBody({ dialog, title, children, closeLabel }: ModalProps) {
    return (
        <>
            <div className={cn(css.header, { [css.noTitle]: !title })}>
                {title && (
                    <Text tag="h2" size="major-sm">
                        {title}
                    </Text>
                )}
                <Button
                    size="large"
                    aria-label={closeLabel}
                    className={css.closeBtn}
                    variant="quaternary"
                    startIcon={X}
                    onClick={dialog.hide}
                />
            </div>
            <div className={css.contentContainer}>{children}</div>
        </>
    )
}
